import { useMotionValueEvent, useScroll } from "motion/react"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

// Small fixed offset (not a % of root height) so a short section's
// neighbour can't cross the line while the short section still occupies
// the top of the view, e.g. right after landing on it via scroll-to.
export const DEFAULT_ACTIVE_OFFSET_PX = 96

export const useActiveSection = <SectionKey extends string>(
  sectionKeys: SectionKey[],
  options?: {
    // Set when sections scroll inside a container, not the window - attach
    // `registerScrollRoot` to that container's ref.
    trackScrollRoot?: boolean
    offsetPx?: number
  }
) => {
  const offsetPx = options?.offsetPx ?? DEFAULT_ACTIVE_OFFSET_PX

  const sectionRefs = useRef<Map<SectionKey, HTMLElement>>(new Map())
  const [activeSectionKey, setActiveSectionKey] = useState<SectionKey>()

  // State, not a mutated ref: a conditionally-mounted container (e.g. a
  // drawer portal) isn't there on first run, so `useScroll` needs a new
  // object identity when it appears to (re-)attach instead of giving up.
  const [scrollRootEl, setScrollRootEl] = useState<HTMLElement | null>(null)
  const scrollRootRef = useMemo(
    () => ({ current: scrollRootEl }),
    [scrollRootEl]
  )

  // Picks the section whose top is closest above the line, checked fresh
  // each scroll frame - unlike an IntersectionObserver "enter" event, a fast
  // scroll can't skip past a short section unnoticed. Checks every key
  // instead of stopping early, since `sectionKeys` may not be in document
  // order (e.g. a CMS-authored nav list vs actual page content order).
  const updateActiveSectionKey = useCallback(() => {
    const rootTop = options?.trackScrollRoot
      ? (scrollRootRef.current?.getBoundingClientRect().top ?? 0)
      : 0
    const thresholdY = rootTop + offsetPx

    let nearestKey: SectionKey | undefined
    let nearestTop = Number.NEGATIVE_INFINITY

    for (const key of sectionKeys) {
      const element = sectionRefs.current.get(key)
      if (!element) continue

      const top = element.getBoundingClientRect().top
      if (top <= thresholdY && top > nearestTop) {
        nearestKey = key
        nearestTop = top
      }
    }

    if (nearestKey) setActiveSectionKey(nearestKey)
  }, [sectionKeys, offsetPx, scrollRootRef, options?.trackScrollRoot])

  const { scrollY } = useScroll(
    options?.trackScrollRoot ? { container: scrollRootRef } : undefined
  )

  useMotionValueEvent(scrollY, "change", updateActiveSectionKey)

  useEffect(() => {
    updateActiveSectionKey()
  }, [updateActiveSectionKey])

  const registerSectionRef = useCallback(
    (key: SectionKey) => (element: HTMLElement | null) => {
      if (element) sectionRefs.current.set(key, element)
    },
    []
  )

  const getSectionElement = useCallback(
    (key: SectionKey) => sectionRefs.current.get(key),
    []
  )

  const getScrollRootElement = useCallback(() => scrollRootEl, [scrollRootEl])

  return {
    activeSectionKey,
    registerSectionRef,
    getSectionElement,
    registerScrollRoot: setScrollRootEl,
    getScrollRootElement,
  }
}
